'use client';

import { useState } from 'react';
import Calculator from './components/Calculator';
import PersonalFinanceErrorBoundary from './components/PersonalFinanceErrorBoundary';
import PrequalResult from './components/PrequalResult';
import PrequalResultError from './components/PrequalResultError';
import { AdobeDataContext } from './context/adobeDataContext';
import CalculatorContextProvider from './context/CalculatorContextProvider';
import usePrequalResult from './hooks/usePrequalResult';
import type { IPersonalFinanceComponentProps } from './types';
import getLowAssuranceCookie from './utils/getLowAssuranceCookie';

function PersonalFinanceComponentContent({
  environment,
  adobeData,
  calculatorOverrides,
  compact,
  className,
}: IPersonalFinanceComponentProps) {
  const [forceCalculator, setForceCalculator] = useState(false);
  const prequalEnabled = Boolean(getLowAssuranceCookie(environment));
  const prequalQuery = usePrequalResult(environment, prequalEnabled);
  const isPrequalLoading = prequalEnabled && prequalQuery.isPending;
  const showPrequal =
    prequalQuery.data?.metaData.isFbsEligible === true && !forceCalculator;

  let content = (
    <Calculator
      isPrequalLoading={isPrequalLoading}
      monthlyPaymentOverride={calculatorOverrides?.monthlyPayment}
      downPaymentOverride={calculatorOverrides?.downPayment}
      creditScoreOverride={calculatorOverrides?.creditScore}
      termLengthOverride={calculatorOverrides?.termLength}
      compact={compact}
    />
  );
  if (prequalQuery.isError) {
    content = <PrequalResultError />;
  } else if (showPrequal && prequalQuery.data) {
    content = (
      <PrequalResult
        prequalResult={prequalQuery.data}
        environment={environment}
        onInvalidPrequal={() => setForceCalculator(true)}
      />
    );
  }

  return (
    <div id="personal-finance-component-v2" className={className}>
      <AdobeDataContext.Provider
        value={{
          ...adobeData,
          opQueryString: `op=PFC_${adobeData.originPage}`,
        }}
      >
        {content}
      </AdobeDataContext.Provider>
    </div>
  );
}

export default function PersonalFinanceComponent(
  props: IPersonalFinanceComponentProps,
) {
  return (
    <PersonalFinanceErrorBoundary>
      <CalculatorContextProvider environment={props.environment}>
        <PersonalFinanceComponentContent {...props} />
      </CalculatorContextProvider>
    </PersonalFinanceErrorBoundary>
  );
}
